import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import { BOOKING_URL, PHONE_TEL, PHONE_DISPLAY } from "./Header";
import { GoogleReviewCta } from "./GoogleReviews";

export function Footer() {
  return (
    <footer
      className="relative bg-forest-900 text-cream/85 pt-20 md:pt-28 pb-32 md:pb-20 mt-24"
      data-testid="site-footer"
    >
      <div className="container-app grid gap-14 md:grid-cols-12">
        <div className="md:col-span-5 max-w-md">
          <p className="eyebrow text-brass mb-4">Westbridge Inn &amp; Suites</p>
          <h3 className="font-display text-3xl md:text-4xl text-cream tracking-tight leading-tight">
            The largest and newest lodging in southern Iowa — minutes from the historic square.
          </h3>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-8"
            data-testid="footer-book-now"
          >
            Book Direct <ExternalLink size={14} />
          </a>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.22em] text-brass mb-4">Contact</h4>
          <address className="not-italic space-y-3 text-sm leading-relaxed">
            <p className="flex gap-3">
              <MapPin size={16} className="mt-0.5 flex-none text-brass" />
              <a
                href="https://maps.google.com/?q=117+N+18th+St,+Centerville,+IA+52544"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-cream"
                data-testid="footer-directions-link"
              >
                117 N 18th St
                <br />
                Centerville, IA 52544
              </a>
            </p>
            <p className="flex gap-3">
              <Phone size={16} className="mt-0.5 flex-none text-brass" />
              <a
                href={`tel:${PHONE_TEL}`}
                className="hover:text-cream"
                data-testid="footer-phone-link"
              >
                {PHONE_DISPLAY}
              </a>
            </p>
            <p className="flex gap-3">
              <Mail size={16} className="mt-0.5 flex-none text-brass" />
              <a
                href="mailto:Centerville@westbridgehotels.com"
                className="hover:text-cream break-all"
                data-testid="footer-email-link"
              >
                Centerville@westbridgehotels.com
              </a>
            </p>
            <p className="flex gap-3">
              <Clock size={16} className="mt-0.5 flex-none text-brass" />
              <span>
                24-hour front desk
                <br />
                Check-in 3 PM · Check-out 11 AM
              </span>
            </p>
          </address>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-xs uppercase tracking-[0.22em] text-brass mb-4">Rooms</h4>
          <ul className="space-y-2.5 text-sm">
            {[
              ["Double Queen", "/rooms/double-queen"],
              ["King Room", "/rooms/king"],
              ["Accessible King", "/rooms/accessible-king"],
              ["King Jacuzzi Suite", "/rooms/king-jacuzzi-suite"],
              ["Construction Crew", "/rooms/construction-crew"],
              ["Exterior Access", "/rooms/exterior-access"],
              ["Interior vs Exterior", "/rooms/interior-vs-exterior"],
            ].map(([label, href]) => (
              <li key={href}>
                <Link
                  to={href}
                  className="hover:text-cream"
                  data-testid={`footer-room-${href.split("/").pop()}`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-xs uppercase tracking-[0.22em] text-brass mb-4">Explore</h4>
          <ul className="space-y-2.5 text-sm">
            {[
              ["Amenities", "/amenities"],
              ["Indoor Pool", "/indoor-pool"],
              ["Meetings & Events", "/meetings-events"],
              ["Stay With Us", "/stay"],
              ["Explore Local", "/explore"],
              ["Journal", "/blog"],
              ["FAQ", "/faq"],
              ["About", "/about"],
              ["Contact", "/contact"],
              ["Accessibility", "/accessibility"],
            ].map(([label, href]) => (
              <li key={href}>
                <Link
                  to={href}
                  className="hover:text-cream"
                  data-testid={`footer-link-${href.split("/").filter(Boolean).join("-") || "home"}`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="container-app mt-16">
        <GoogleReviewCta variant="dark" testId="footer-review-cta" />
      </div>

      <div className="container-app mt-12 pt-8 border-t border-forest-700/50 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-cream/60">
        <p>© {new Date().getFullYear()} Westbridge Inn &amp; Suites, Centerville, Iowa. All rights reserved.</p>
        <p>Formerly Motel 60 · Independently owned &amp; operated</p>
      </div>
    </footer>
  );
}
